<template>
  <div class="px-1">
    <NavigationBreadcrumb
      :items="breadcrumbItems"
      :egressProfileId="egress_profile_id"
      :showEgressProfile="true"
      :showMatrix1="true"
      :showMatrix2="true"
      :showMatrix3="true"
      :showStudyPlan="true"
      :showCurricularMesh="true"
    ></NavigationBreadcrumb>
    <template v-if="usesAcademicLoad">
      <h3 class="mt-3 mb-2 text-left">
        {{ academicLoadTitle }}
        <small v-if="egressProfile" class="text-muted">
          - {{ egressProfile.name }}
        </small>
      </h3>
      <AcademicLoadTable
        :egress_profile_id="egress_profile_id"
        :allows_crud="allowsCrud"
      >
        <template #actions>
          <button
            type="button"
            class="btn btn-primary btn-sm mr-2"
            :disabled="downloading"
            @click="downloadExcel"
          >
            {{ $t("studyPlan.academicLoad.downloadExcel") }}
          </button>
        </template>
      </AcademicLoadTable>
    </template>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Carga Académica de un perfil de egreso (solo $usesAcademicLoad): el Plan de Estudio con
// el formato de la hoja "Carga Academica" del Anexo 4 PUCV. Va entre el Plan de Estudio y
// la Malla en el flujo del perfil.
// Legacy (Bootstrap-Vue): NavigationBreadcrumb es el breadcrumb compartido de la app.
import { computed, getCurrentInstance, ref, watch } from "vue";
import * as names from "@/store/names";
import { downloadFileFromResponse, showLoadingAlert } from "@/utils/utils";
import NavigationBreadcrumb from "@/components/reusable/NavigationBreadcrumb";
import AcademicLoadTable from "./AcademicLoadTable";

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;
const egress_profile_id = Number(proxy.$route.params.egress_profile_id);

const user = computed(() => store.getters.getUser);
const institution = computed(() => store.getters.getInstitution);
const usesAcademicLoad = computed(() =>
  proxy.$usesAcademicLoad(institution.value)
);
const egressProfile = computed(() =>
  store.getters[names.EGRESS_PROFILES].find((x) => x.id == egress_profile_id)
);
const career = computed(() =>
  egressProfile.value
    ? store.getters[names.CAREERS].find(
        (x) => x.id == egressProfile.value.career
      )
    : null
);

// Nombre alternativo manual (alternative_names_update); por defecto "Carga Académica"
const academicLoadTitle = computed(() =>
  proxy.$getVisibleNames(
    "manual.academic_load",
    false,
    proxy.$t("studyPlan.academicLoad.title")
  )
);

const breadcrumbItems = computed(() => [
  { text: proxy.$t("reusable.breadcrumb.home"), to: "/home" },
  {
    text: proxy.$getVisibleNames("mesh.career", true, "Programas"),
    to: "/careers",
  },
  {
    text: academicLoadTitle.value,
    dropdownOptions: [
      "showEgressProfile",
      "showMatrix1",
      "showMatrix2",
      "showMatrix3",
      "showStudyPlan",
      "showCurricularMesh",
    ],
  },
]);

// Misma regla que StudyPlanView: quién puede editar el plan de estudio del perfil
const canUpdate = computed(() => {
  if (!user.value) return false;
  const group_names = user.value.group_names;
  if (
    user.value.is_superuser ||
    [
      "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
      "DIRECTIVOS ACADÉMICOS (CAMPUS)",
    ].some((group) => group_names.includes(group))
  )
    return true;
  if (!career.value || group_names.includes("ADMINISTRATIVOS INSTRUCCIONALES"))
    return false;
  return (
    [
      "DIRECTIVOS FACULTAD O ESCUELA",
      "DIRECTORES DE CARRERA",
      "DIRECTORES DE CARRERA (CAMPUS)",
    ].some((group) => group_names.includes(group)) &&
    (proxy.$hasObjectPermission("mesh.faculty", "change", career.value.faculty) ||
      proxy.$hasObjectPermission(
        "mesh.egressprofile",
        "change",
        egress_profile_id
      ))
  );
});
const allowsCrud = computed(
  () =>
    !!egressProfile.value &&
    !egressProfile.value.is_closed &&
    canUpdate.value &&
    proxy.$hasPermissions("mesh.change_egressprofile")
);

// La vista es solo para las instituciones con Carga Académica
watch(
  institution,
  (value) => {
    if (value && !proxy.$usesAcademicLoad(value))
      proxy.$router.push({ name: "Page403" });
  },
  { immediate: true }
);

const downloading = ref(false);

function downloadExcel() {
  downloading.value = true;
  showLoadingAlert(
    proxy.$t("reusable.common.generatingExcel"),
    proxy.$t("reusable.common.pleaseWaitFile")
  );
  const finish = () => {
    downloading.value = false;
  };
  proxy.$restful
    .Post(
      "/mesh/excel_report_academic_load/",
      { egress_profile_id: egress_profile_id },
      { responseType: "blob" }
    )
    .then((response) => {
      proxy.$swal.close();
      downloadFileFromResponse(
        response,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        finish,
        finish
      );
    })
    .catch(() => {
      finish();
      proxy.$swal.close();
      proxy.$swal.fire(
        proxy.$t("reusable.common.error"),
        proxy.$t("reusable.common.problemGenerating"),
        "error"
      );
    });
}

store
  .dispatch(names.FETCH_EGRESS_PROFILE, egress_profile_id)
  .then((egress_profile) =>
    store.dispatch(names.FETCH_CAREER, egress_profile.career)
  );
</script>
